package com.sweetlime.bingeamigo.presentation.features.library

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.LibraryItem
import com.sweetlime.bingeamigo.presentation.components.PosterTile
import com.sweetlime.bingeamigo.presentation.components.SegmentedPills
import com.sweetlime.bingeamigo.presentation.ui.BrandColors

/**
 * Redesigned Library screen.
 *
 * Top pills switch media type (Movies / TV Shows) — amber active.
 * Count tiles switch collection; labels adapt per media type — teal active (tracking state).
 * Movies:   Watchlist / Watched / Favorites
 * TV Shows: Tracking / Completed / Favorites
 *
 * onRemove receives (item, mediaType, collection) so the caller can dispatch to the
 * right ViewModel remove method.
 * onItemClick receives (item, mediaType) so the caller can push the right detail screen.
 */
@Composable
fun LibraryScreen(
    movieWatchlist: List<LibraryItem>,
    movieWatched: List<LibraryItem>,
    movieFavorites: List<LibraryItem>,
    tvTracking: List<LibraryItem>,
    tvCompleted: List<LibraryItem>,
    tvFavorites: List<LibraryItem>,
    onItemClick: (LibraryItem, String) -> Unit,
    onRemove: (LibraryItem, String, String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var mediaType by remember { mutableStateOf("TV Shows") }
    var collection by remember { mutableStateOf("Watchlist") }

    LaunchedEffect(mediaType) {
        collection = if (mediaType == "Movies") "Watchlist" else "Tracking"
    }

    val collectionLabels = if (mediaType == "Movies")
        listOf("Watchlist", "Watched", "Favorites")
    else
        listOf("Tracking", "Completed", "Favorites")

    val items = when {
        mediaType == "Movies" && collection == "Watchlist"   -> movieWatchlist
        mediaType == "Movies" && collection == "Watched"     -> movieWatched
        mediaType == "Movies" && collection == "Favorites"   -> movieFavorites
        mediaType == "TV Shows" && collection == "Tracking"  -> tvTracking
        mediaType == "TV Shows" && collection == "Completed" -> tvCompleted
        mediaType == "TV Shows" && collection == "Favorites" -> tvFavorites
        else -> emptyList()
    }

    val counts = if (mediaType == "Movies")
        listOf(movieWatchlist.size, movieWatched.size, movieFavorites.size)
    else
        listOf(tvTracking.size, tvCompleted.size, tvFavorites.size)

    Column(modifier = modifier.fillMaxSize().background(BrandColors.Background)) {
        Column(Modifier.padding(start = 16.dp, end = 16.dp, top = 10.dp)) {
            SegmentedPills(
                options = listOf("TV Shows", "Movies"),
                selected = mediaType,
                onSelect = { mediaType = it },
                height = 40,
            )
            Spacer(Modifier.height(10.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                collectionLabels.forEachIndexed { i, label ->
                    val n = counts[i]
                    val active = collection == label
                    Surface(
                        onClick = { collection = label },
                        modifier = Modifier.weight(1f).height(46.dp),
                        shape = androidx.compose.foundation.shape.RoundedCornerShape(11.dp),
                        color = if (active) BrandColors.Secondary.copy(alpha = 0.13f) else BrandColors.Card,
                        border = androidx.compose.foundation.BorderStroke(
                            1.dp, if (active) BrandColors.Secondary else Color(0xFF2A2A2E),
                        ),
                    ) {
                        Column(
                            Modifier.padding(4.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center,
                        ) {
                            Text(
                                label,
                                color = if (active) Color(0xFF2FD0D0) else BrandColors.TextSecondary,
                                fontWeight = FontWeight.ExtraBold,
                                fontSize = 13.5.sp,
                            )
                            Text(
                                n.toString(),
                                color = if (active) Color(0xFF2FD0D0) else BrandColors.TextSecondary,
                                fontSize = 11.5.sp,
                            )
                        }
                    }
                }
            }
        }

        LazyVerticalGrid(
            columns = GridCells.Fixed(2),
            contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 16.dp, bottom = 100.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            items(items, key = { it.id }) { item ->
                PosterTile(
                    posterUrl = "https://image.tmdb.org/t/p/w342${item.posterPath.orEmpty()}",
                    title = item.title,
                    rating = null,
                    onClick = { onItemClick(item, mediaType) },
                    overlay = {
                        Box(
                            Modifier
                                .align(Alignment.TopEnd)
                                .padding(8.dp)
                                .size(28.dp)
                                .clip(CircleShape)
                                .background(Color(0x80000000))
                                .clickable { onRemove(item, mediaType, collection) },
                            contentAlignment = Alignment.Center,
                        ) {
                            Icon(Icons.Filled.Close, "Remove", tint = Color.White, modifier = Modifier.size(15.dp))
                        }
                    },
                )
            }
        }
    }
}
